import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { PageNotFoundComponent } from './core/page-not-found/page-not-found.component';
import { adminGuard } from './shared/guards/admin.guard';
import { authGuard } from './shared/guards/auth-guard.guard';
import { debugEmailListGuard } from './shared/guards/debug-email-list.guard';
import { homeGuard } from './shared/guards/home-guard.guard';
import { marketGuard } from './shared/guards/market.guard';
import { ownerGuard } from './shared/guards/owner.guard';
import { profileGuard } from './shared/guards/profile-guard.guard';
import { providerGuard } from './shared/guards/provider.guard';
import { registerGuard } from './shared/guards/register.guard';

const routes: Routes = [
  // routings without language fragment for edgecases
  {
    path: 'debug-email-list',
    loadChildren: () =>
      import('./features/debug-email-list/debug-email-list.module').then(
        m => m.DebugEmailListModule
      ),
    canActivate: [debugEmailListGuard],
  },
  {
    path: 'login',
    loadChildren: () =>
      import('./features/login/login.module').then(m => m.LoginModule),
  },
  {
    path: 'account-activate',
    loadChildren: () =>
      import('./features/account-activate/account-activate.module').then(
        m => m.AccountActivateModule
      ),
  },
  {
    path: 'account-recovery',
    loadChildren: () =>
      import('./features/account-recovery/account-recovery.module').then(
        m => m.AccountRecoveryModule
      ),
  },
  {
    path: 'email-change',
    loadChildren: () =>
      import('./features/email-change/email-change.module').then(
        m => m.EmailChangeModule
      ),
  },
  {
    path: 'register-for-owner',
    loadChildren: () =>
      import('./features/register-for-owner/register-for-owner.module').then(
        m => m.RegisterForOwnerModule
      ),
    canActivate: [registerGuard],
  },
  {
    path: 'register-for-provider',
    loadChildren: () =>
      import(
        './features/register-for-provider/register-for-provider.module'
      ).then(m => m.RegisterForProviderModule),
    canActivate: [registerGuard],
  },
  {
    path: 'register-success',
    loadChildren: () =>
      import('./features/register-success/register-success.module').then(
        m => m.RegisterSuccessModule
      ),
  },

  // routings
  { path: '', redirectTo: 'en/home', pathMatch: 'full' },
  {
    path: ':lang',
    redirectTo: ':lang/home',
    pathMatch: 'full',
  },
  {
    path: ':lang/home',
    loadChildren: () =>
      import('./features/home/home-routing.module').then(
        m => m.HomeRoutingModule
      ),
    canActivate: [homeGuard],
  },
  {
    path: ':lang/material-test',
    loadChildren: () =>
      import('./features/material-test/material-test-routing.module').then(
        m => m.MaterialTestRoutingModule
      ),
    canActivate: [authGuard],
  },
  {
    path: ':lang/login',
    loadChildren: () =>
      import('./features/login/login.module').then(m => m.LoginModule),
  },
  {
    path: ':lang/register-for-owner',
    loadChildren: () =>
      import('./features/register-for-owner/register-for-owner.module').then(
        m => m.RegisterForOwnerModule
      ),
    canActivate: [registerGuard],
  },
  {
    path: ':lang/recommend-owner',
    loadChildren: () =>
      import('./features/recommend-owner/recommend-owner.module').then(
        m => m.RecommendOwnerModule
      ),
  },
  {
    path: ':lang/recommend-provider',
    loadChildren: () =>
      import('./features/recommend-provider/recommend-provider.module').then(
        m => m.RecommendProviderModule
      ),
  },
  {
    path: ':lang/contact',
    loadChildren: () =>
      import('./features/contact/contact.module').then(m => m.ContactModule),
  },
  {
    path: ':lang/provider/:provider',
    loadChildren: () =>
      import('./features/provider/provider.module').then(m => m.ProviderModule),
  },
  {
    path: ':lang/payment-successful',
    loadChildren: () =>
      import('./features/payment-successful/payment-successful.module').then(
        m => m.PaymentSuccessfulModule
      ),
    canActivate: [providerGuard],
  },
  {
    path: ':lang/provider/:provider/:id',
    loadChildren: () =>
      import('./features/provider-details/provider-details.module').then(
        m => m.ProviderDetailsModule
      ),
    // canActivate: [providerDetailsGuard],
  },
  {
    path: ':lang/search-adventure',
    loadChildren: () =>
      import('./features/search-adventure/search-adventure.module').then(
        m => m.SearchAdventureModule
      ),
    canActivate: [authGuard],
  },
  {
    path: ':lang/adventure/:id',
    loadChildren: () =>
      import('./features/adventure-details/adventure-details.module').then(
        m => m.AdventureDetailsModule
      ),
    // canActivate: [providerDetailsGuard],
  },
  {
    path: ':lang/register-for-provider',
    loadChildren: () =>
      import(
        './features/register-for-provider/register-for-provider.module'
      ).then(m => m.RegisterForProviderModule),
    canActivate: [registerGuard],
  },
  {
    path: ':lang/register-success',
    loadChildren: () =>
      import('./features/register-success/register-success.module').then(
        m => m.RegisterSuccessModule
      ),
  },
  {
    path: ':lang/media-offer',
    loadChildren: () =>
      import('./features/media-offer/media-offer.module').then(
        m => m.MediaOfferModule
      ),
  },
  {
    path: ':lang/globomarket',
    loadChildren: () =>
      import('./features/globo-market/globo-market.module').then(
        m => m.GloboMarketModule
      ),
    canActivate: [marketGuard],
  },
  {
    path: ':lang/globomarket/item/:id',
    loadChildren: () =>
      import('./features/market-item/market-item.module').then(
        m => m.MarketItemModule
      ),
    canActivate: [marketGuard],
  },
  {
    path: ':lang/profile',
    canActivate: [profileGuard],
    children: [],
  },
  {
    path: ':lang/profile-owner',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/owner-profile/owner-profile.module').then(
        m => m.OwnerProfileModule
      ),
  },
  {
    path: ':lang/profile-owner/:id',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/owner-profile/owner-profile.module').then(
        m => m.OwnerProfileModule
      ),
  },
  {
    path: ':lang/profile-provider',
    //canActivate: [authGuard],
    loadChildren: () =>
      import('./features/provider-profile/provider-profile.module').then(
        m => m.ProviderProfileModule
      ),
    canActivate: [providerGuard],
  },
  {
    path: ':lang/admin',
    //canActivate: [authGuard],
    loadChildren: () =>
      import('./features/admin/admin.module').then(m => m.AdminModule),
    canActivate: [adminGuard],
  },
  {
    path: ':lang/profile-dogs',
    loadChildren: () =>
      import('./features/profile-dogs/profile-dogs.module').then(
        m => m.ProfileDogsModule
      ),
    canActivate: [ownerGuard],
  },
  {
    path: ':lang/profile-ads',
    loadChildren: () =>
      import('./features/profile-ads/profile-ads.module').then(
        m => m.ProfileAdsModule
      ),
    canActivate: [ownerGuard],
  },
  {
    path: ':lang/profile-programs',
    loadChildren: () =>
      import('./features/profile-programs/profile-programs.module').then(
        m => m.ProfileProgramsModule
      ),
    canActivate: [ownerGuard],
  },
  {
    path: ':lang/my-adventure',
    loadChildren: () =>
      import('./features/my-adventure/my-adventure.module').then(
        m => m.MyAdventureModule
      ),
    canActivate: [ownerGuard],
  },
  {
    path: ':lang/faq-owner',
    data: {
      type: 'owner',
    },
    loadChildren: () =>
      import('./features/faq/faq.module').then(m => m.FAQModule),
  },
  {
    path: ':lang/programs',
    loadChildren: () =>
      import('./features/programs/programs.module').then(m => m.ProgramsModule),
  },
  {
    path: ':lang/programs/:id',
    loadChildren: () =>
      import('./features/program-details/program-details.module').then(
        m => m.ProgramDetailsModule
      ),
    // canActivate: [providerDetailsGuard],
  },
  {
    path: ':lang/faq-provider',
    data: {
      type: 'provider',
    },
    loadChildren: () =>
      import('./features/faq/faq.module').then(m => m.FAQModule),
  },
  {
    path: ':lang/blog',
    loadChildren: () =>
      import('./features/blog/blog.module').then(m => m.BlogModule),
    // canActivate: [ownerAlwaysLoginGuard],
  },
  {
    path: ':lang/blog/:id/:title',
    loadChildren: () =>
      import('./features/blog/one-blog-full-page/one-blog-full-page.module').then(m => m.OneBlogFullPageModule),
    // canActivate: [ownerAlwaysLoginGuard],
  },
  {
    path: ':lang/blog/:id',
    loadChildren: () =>
      import('./features/blog/one-blog-full-page/one-blog-full-page.module').then(m => m.OneBlogFullPageModule),
    // canActivate: [ownerAlwaysLoginGuard],
  },

  {
    path: ':lang/unsubscribe',
    loadChildren: () =>
      import('./features/unsubscribe/unsubscribe.module').then(
        m => m.UnsubscribeModule
      ),
  },
  {
    path: ':lang/add-provider-ad',
    loadChildren: () =>
      import('./features/add-provider-ad/add-provider-ad.module').then(
        m => m.AddProviderAdModule
      ),
    canActivate: [providerGuard],
  },
  {
    path: ':lang/add-dog',
    loadChildren: () =>
      import('./features/add-dog/add-dog.module').then(m => m.AddDogModule),
    canActivate: [ownerGuard],
  },
  {
    path: ':lang/delete-me',
    loadChildren: () =>
      import('./features/delete-me/delete-me.module').then(m => m.DeleteMeModule),
    canActivate: [],
  },
  {
    path: ':lang/add-owner-ad',
    loadChildren: () =>
      import('./features/add-owner-ad/add-owner-ad.module').then(
        m => m.AddOwnerAdModule
      ),
    canActivate: [ownerGuard],
  },
  {
    path: ':lang/add-program',
    loadChildren: () =>
      import('./features/add-program/add-program.module').then(
        m => m.AddProgramModule
      ),
    canActivate: [authGuard],
  },
  {
    path: ':lang/add-adventure',
    loadChildren: () =>
      import('./features/add-adventure/add-advenure.module').then(
        m => m.AddAdventureModule
      ),
    canActivate: [authGuard],
  },
  {
    path: ':lang/update-program/:id',
    loadChildren: () =>
      import('./features/add-program/add-program.module').then(
        m => m.AddProgramModule
      ),
    canActivate: [authGuard],
  },
  {
    path: ':lang/update-adventure/:id',
    loadChildren: () =>
      import('./features/add-adventure/add-advenure.module').then(
        m => m.AddAdventureModule
      ),
    canActivate: [authGuard],
  },
  {
    path: ':lang/edit-owner-ad',
    loadChildren: () =>
      import('./features/edit-owner-ad/edit-owner-ad.module').then(
        m => m.EditOwnerAdModule
      ),
    canActivate: [ownerGuard],
  },
  {
    path: ':lang/add-provider',
    loadChildren: () =>
      import('./features/add-provider/add-provider.module').then(
        m => m.AddProviderModule
      ),
    canActivate: [authGuard],
  },
  {
    path: ':lang/forgot-password',
    loadChildren: () =>
      import('./features/forgot-password/forgot-password.module').then(
        m => m.ForgotPasswordModule
      ),
  },
  {
    path: ':lang/account-activate',
    loadChildren: () =>
      import('./features/account-activate/account-activate.module').then(
        m => m.AccountActivateModule
      ),
  },
  {
    path: ':lang/account-recovery',
    loadChildren: () =>
      import('./features/account-recovery/account-recovery.module').then(
        m => m.AccountRecoveryModule
      ),
  },
  {
    path: ':lang/useful',
    loadChildren: () =>
      import('./features/useful/useful.module').then(m => m.UsefulModule),
    // canActivate: [ownerAlwaysLoginGuard],
  },
  {
    path: ':lang/comment/:id',
    loadChildren: () =>
      import('./features/comment-full-page/comment-full-page.module').then(m => m.CommentFullPageModule),
  },
  {
    path: ':lang/gdpr',
    loadChildren: () =>
      import('./features/gdpr/gdpr.module').then(m => m.GdprModule),
  },
  {
    path: ':lang/airline-rules',
    loadChildren: () =>
      import('./features/airline-rules/airline-rules.module').then(m => m.AirlineRulesModule),
  },
  {
    path: ':lang/country-rules',
    loadChildren: () =>
      import('./features/country-rules/country-rules.module').then(m => m.CountryRulesModule),
  },
  {
    path: ':lang/about-us',
    loadChildren: () =>
      import('./features/about-us/about-us.module').then(m => m.AboutUsModule),
  },
  { path: '**', component: PageNotFoundComponent }, // Wildcard route for a 404 page
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
